import { AfterViewChecked, ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnDestroy } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { ContentLink } from '@frontend/vanilla/core';
import { keyBy } from 'lodash-es';
import { BehaviorSubject, noop } from 'rxjs';

import { TemplatesWithThresholds } from '../../option-group/option-group-list.component';
import { TopEventsMappingService } from '../top-events-mapping.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { EventDataForOptionPanel, PodType, TopEventsPodData } from '../top-events.model';

@Component({
    selector: 'ms-top-events-single',
    templateUrl: 'top-events-single.component.html',
    styleUrl: 'top-events-single.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TopEventsSingleComponent implements OnChanges, OnDestroy, AfterViewChecked {
    @HostBinding('class') className = 'top-events-pod-single';
    @HostBinding('class.market-header-hidden') hideMarketHeaderClass = false;
    @HostBinding('class.pod-header-hidden') get podHeaderHidden() {
        return this.pod?.hidePodHeader;
    }

    @Input() pod: TopEventsPodData;
    @Input() index: number;

    vms$ = new BehaviorSubject<EventDataForOptionPanel | undefined>(undefined);

    hideMarketHeader = false;
    link: ContentLink;
    limit: number;
    tracking: string;
    trackingModuleType = PodType.Single;
    contentRendered = false;
    thresholds: TemplatesWithThresholds;

    constructor(
        public sitecore: Sitecore,
        private topEventsPodService: TopEventsMappingService,
        private topEventsTrackingService: TopEventsTrackingService,
        private urlHelper: UrlHelperService,
        private eventDetailsConfig: EventDetailsConfig,
        private marketGroupingConfig: MarketGroupingConfig,
    ) {}

    ngAfterViewChecked(): void {
        this.contentRendered = true;
    }

    ngOnChanges(): void {
        this.pod.updateHandler = this.updateViewModel;

        if (this.pod.events?.length) {
            this.updateViewModel();
            this.limit = this.pod.marketsBeforeShowMore ? this.pod.marketsBeforeShowMore : (this.vms$.value?.preparedOptionGoups.length ?? 0);
        }
        this.hideMarketHeader = this.hideMarketHeaderClass = this.pod.hideMarketHeader;
    }

    showMarkets(): void {
        this.limit = this.vms$.value!.preparedOptionGoups.length;
        this.topEventsTrackingService.trackShowMoreLess(this.pod, this.index, false);
    }

    trackBottomLink(): void {
        this.topEventsTrackingService.trackBottomLink(this.pod, this.index);
    }

    private updateViewModel = (): void => {
        const vm = this.topEventsPodService.getPodEventData({
            eventData: this.pod.events[0],
            sourceFixtures: this.pod.sourceFixtures,
            selectionTextOverride: undefined,
            isDetailed: true,
            allowDuplicates: true,
            blackListMarketGroupIds: this.pod.blackListMarketGroupIds,
            orderAsConfigured: !this.pod.marketParameters?.length,
        });

        this.tracking = `TopEvents/Sport/${vm.eventModel?.sport.id}|${PodType.Single}`;
        this.thresholds = this.getTresholds(vm.eventModel);
        this.link = { url: this.urlHelper.getEventUrl(vm.eventModel), text: this.sitecore.event.SeeAll } as ContentLink;

        this.vms$.next(vm);
    };

    private getTresholds(event: EventModel): TemplatesWithThresholds {
        const config = this.eventDetailsConfig.marketTemplatesWithThresholds[event?.sport.id];
        const templates = config ? keyBy(config, (id) => id) : {};

        return { templates, thresholds: this.marketGroupingConfig.optionCollapseSize };
    }

    ngOnDestroy(): void {
        this.pod.updateHandler = noop;
    }
}
